import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
} from 'firebase/firestore';

/**
 * Audit row for $100 dinner / social featuring opt-in.
 * Firestore rules: create/delete/read own docs (see firestore.rules).
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {{ uid: string, displayName?: string|null, email?: string|null }} user
 * @param {{ id: string, posterNickname?: string|null }} poster
 * @param {string} source
 */
export async function recordDinnerGiveawayOptIn(firestore, user, poster, source) {
  if (!user?.uid || !poster?.id) return;
  /** Same field as nickname badge / `mapSharedPosterToFeedAd` (100, 50, 25, 5, 1). Admin or seedBountyValues sets the real $; client create must keep null (see firestore.rules). */
  const bountyAmount = null;
  await addDoc(collection(firestore, 'dinnerGiveawayOptIns'), {
    userId: user.uid,
    createdAt: serverTimestamp(),
    posterId: poster.id,
    posterNickname: poster.posterNickname ?? null,
    displayName: user.displayName ?? null,
    email: user.email ?? null,
    source,
    /** When true, collage appears in THIS WEEK'S $100 DROPS (set via Firebase console or admin). */
    postedInFeed: false,
    /** When true, collage is the single WEEKLY FEATURED Berkeley carousel card (set via Firebase console or admin). */
    berkeleyfeature: false,
    /** When true, collage is the single WEEKLY FEATURED SF Marina carousel card (set via Firebase console or admin). */
    sfmarinafeature: false,
    /** When true, collage is the single WEEKLY FEATURED SF North Beach carousel card (set via Firebase console or admin). */
    sfnorthbeachfeature: false,
    /** When true, collage is the single WEEKLY FEATURED SF SOMA carousel card (set via Firebase console or admin). */
    sfsomafeature: false,
    /** When true, collage is the single WEEKLY FEATURED SF Downtown carousel card (set via Firebase console or admin). */
    sfdowntownfeature: false,
    bountyAmount,
  });
}

/**
 * Remove all opt-in rows for this user + poster (e.g. Your collages primary opt-out).
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 * @param {string} posterId
 */
export async function deleteDinnerGiveawayOptInsForPoster(firestore, userId, posterId) {
  if (!userId || !posterId) return;
  const q = query(
    collection(firestore, 'dinnerGiveawayOptIns'),
    where('userId', '==', userId),
    where('posterId', '==', posterId),
  );
  const snap = await getDocs(q);
  await Promise.all(
    snap.docs.map((d) => deleteDoc(doc(firestore, 'dinnerGiveawayOptIns', d.id))),
  );
}

/**
 * Only `primaryCollageId` may have promoteOnLuvwtfSocials true. Others are cleared (Firestore write uses returned posters).
 *
 * @returns {{ posters: Array, clearedPosterIds: string[] }}
 */
export function normalizeGiveawayToPrimaryOnly(posters, primaryCollageId) {
  if (!primaryCollageId || !Array.isArray(posters)) {
    return { posters: posters || [], clearedPosterIds: [] };
  }
  const clearedPosterIds = [];
  const next = posters.map((p) => {
    if (p.id === primaryCollageId) return p;
    if (p.promoteOnLuvwtfSocials === true) {
      clearedPosterIds.push(p.id);
      return { ...p, promoteOnLuvwtfSocials: false };
    }
    return p;
  });
  return { posters: next, clearedPosterIds };
}

/**
 * When changing primary: the new primary inherits dinner giveaway iff the previous primary had it;
 * every other collage is opted out. `clearedPosterIds` is used to delete `dinnerGiveawayOptIns` rows.
 *
 * @returns {{ posters: Array, clearedPosterIds: string[] }}
 */
export function applyPrimarySwitchGiveaway(posters, newPrimaryId, oldWasInGiveaway) {
  if (!newPrimaryId || !Array.isArray(posters)) {
    return { posters: posters || [], clearedPosterIds: [] };
  }
  const clearedPosterIds = [];
  const next = posters.map((p) => {
    if (p.id === newPrimaryId) {
      if (p.promoteOnLuvwtfSocials === true && !oldWasInGiveaway) {
        clearedPosterIds.push(p.id);
      }
      return { ...p, promoteOnLuvwtfSocials: oldWasInGiveaway };
    }
    if (p.promoteOnLuvwtfSocials === true) {
      clearedPosterIds.push(p.id);
      return { ...p, promoteOnLuvwtfSocials: false };
    }
    return p;
  });
  return { posters: next, clearedPosterIds };
}

export async function deleteDinnerGiveawayOptInsForPosters(firestore, userId, posterIds) {
  const unique = [...new Set((posterIds || []).filter(Boolean))];
  await Promise.all(unique.map((id) => deleteDinnerGiveawayOptInsForPoster(firestore, userId, id)));
}
